import { Flex, Link } from '@chakra-ui/react';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { isTabAllowed, useGetLoggedInUser } from '@revelio/auth';
import { TourClasses } from '@revelio/core';

import {
  ANALYTICS_VIEWS,
  type AnalyticsEntity,
} from '../utils/revelio-primary-filter-entity-switch';

type IndicatorRect = { left: number; width: number };

// Persists across route-level remounts so the indicator can slide-animate from
// its previous position when the Header re-renders for a new tab's page.
let lastIndicatorRect: IndicatorRect | null = null;

const formatTransform = (rect: IndicatorRect) =>
  `translate3d(${rect.left}px, 0, 0) scaleX(${rect.width})`;

export const AnalyticsViewTabs = ({ entity }: { entity: AnalyticsEntity }) => {
  const location = useLocation();
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const indicatorRef = useRef<HTMLDivElement>(null);

  const { loggedInUser } = useGetLoggedInUser();

  const visibleViews = useMemo(
    () =>
      ANALYTICS_VIEWS.filter((v) =>
        isTabAllowed(v.serverTab, loggedInUser?.tabs ?? [])
      ),
    [loggedInUser]
  );

  const activeIndex = visibleViews.findIndex(
    (view) => location.pathname === `/${view.paths[entity]}`
  );

  const slideIndicatorTo = useCallback(
    (tab: HTMLAnchorElement | null | undefined) => {
      const el = indicatorRef.current;
      if (!tab || !el) return;
      const next: IndicatorRect = {
        left: tab.offsetLeft,
        width: tab.offsetWidth,
      };
      el.style.opacity = '1';
      el.style.transform = formatTransform(next);
      lastIndicatorRect = next;
    },
    []
  );

  useLayoutEffect(() => {
    const el = indicatorRef.current;
    if (!el) return;
    if (activeIndex < 0) {
      el.style.opacity = '0';
      lastIndicatorRect = null;
      return;
    }
    // rAF ensures the browser paints the cached "from" transform once before
    // we mutate to the target — without this both transforms flush in one
    // paint and CSS has nothing to animate from. Animating `transform` keeps
    // the slide on the compositor so chart-loading work can't drop frames.
    const raf = requestAnimationFrame(() => {
      slideIndicatorTo(tabRefs.current[activeIndex]);
    });
    return () => cancelAnimationFrame(raf);
  }, [activeIndex, slideIndicatorTo]);

  return (
    <Flex
      as="nav"
      aria-label="Analytics views"
      className={TourClasses.TOUR_ANALYTICS_VIEW_TABS_CLASS}
      data-testid="analytics-view-tabs"
      alignItems="stretch"
      gap={5}
      height="32px"
      position="relative"
    >
      {visibleViews.map((view, i) => {
        const path = `/${view.paths[entity]}${location.search}`;
        const isActive = activeIndex === i;
        return (
          <Link
            key={view.name}
            ref={(el: HTMLAnchorElement | null) => {
              tabRefs.current[i] = el;
            }}
            as={NavLink}
            to={path}
            onClick={() => {
              // Move the indicator immediately on click. The OLD AnalyticsViewTabs
              // stays mounted while React renders the new route's (heavy) chart
              // subtree, so its indicator div is in the DOM and the GPU-driven
              // `transform` transition runs unblocked. By the time the new
              // instance mounts, lastIndicatorRect is at the new position and
              // it renders in place — no jump, no waiting on the commit.
              slideIndicatorTo(tabRefs.current[i]);
            }}
            data-testid={`analytics-view-tab-${view.name.toLowerCase().replace(/\s+/g, '-')}`}
            display="flex"
            alignItems="center"
            fontSize="14px"
            fontWeight="600"
            color={isActive ? 'text.primary' : '#8F9CB2'}
            whiteSpace="nowrap"
            _hover={{ color: 'text.primary', textDecoration: 'none' }}
          >
            {view.name}
          </Link>
        );
      })}
      <div
        ref={indicatorRef}
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '-1px',
          left: 0,
          width: '1px',
          height: '2px',
          background: 'var(--chakra-colors-green-500)',
          transform: lastIndicatorRect
            ? formatTransform(lastIndicatorRect)
            : 'translate3d(0, 0, 0) scaleX(0)',
          transformOrigin: 'left center',
          transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'transform',
          pointerEvents: 'none',
          opacity: lastIndicatorRect ? 1 : 0,
        }}
      />
    </Flex>
  );
};
